
import React, { useState, useEffect } from 'react';
import { db } from '@/services/mockDb';
import { FinancialRecord } from '@/types';
import { 
  TrendingUp, TrendingDown, DollarSign, Calendar, Plus, 
  Search, Filter, Trash2, Edit2, CheckCircle, AlertCircle, 
  CreditCard, User, FileText, ChevronDown, ChevronUp
} from 'lucide-react';
import { FINANCIAL_CATEGORIES, PAYMENT_METHODS } from '@/constants';
import { v4 as uuidv4 } from 'uuid';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const Financials: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'dashboard' | 'records'>('dashboard');
  const [records, setRecords] = useState<FinancialRecord[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState<Partial<FinancialRecord>>({
    type: 'expense',
    category: '',
    status: 'pending',
    amount: 0,
    isRecurring: false
  });
  const [isSaving, setIsSaving] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [filterMonth, setFilterMonth] = useState(new Date().toISOString().slice(0, 7));

  useEffect(() => {
    loadData();
  }, [filterMonth]);

  const loadData = async () => {
    // @ts-ignore
    const statement = await db.getFinancialStatement(filterMonth);
    setRecords(statement.records);
    setStats(statement.summary);
  };

  const handleOpenNew = (type: 'income' | 'expense') => {
    setFormData({
      type,
      category: '',
      description: '',
      amount: 0,
      dueDate: new Date().toISOString().split('T')[0],
      status: 'pending',
      isRecurring: false,
      metadata: {}
    });
    setShowModal(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const amountValue = typeof formData.amount === 'string' ? parseFloat(formData.amount) : formData.amount;
      const record: FinancialRecord = {
        ...formData as FinancialRecord,
        id: formData.id || uuidv4(),
        amount: amountValue || 0,
      };

      if (formData.id) {
        // @ts-ignore
        await db.updateFinancialRecord(record);
      } else {
        // @ts-ignore
        await db.addFinancialRecord(record);
      }
      
      await loadData();
      setShowModal(false);
    } catch (err) {
      alert("Erro ao salvar registro.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (confirm("Tem certeza que deseja excluir este lançamento?")) {
      // @ts-ignore
      await db.removeFinancialRecord(id);
      loadData();
    }
  };

  const handleMarkPaid = async (record: FinancialRecord) => {
    const updated = { ...record, status: 'paid', paidDate: new Date().toISOString() };
    if (!record.linkedEntityId) { 
       // @ts-ignore
       await db.updateFinancialRecord(updated as FinancialRecord);
       loadData();
    }
  };

  const formatMoney = (val: number) => val.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  const StatCard = ({ title, value, color, icon: Icon }: any) => (
    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex items-center justify-between">
      <div>
        <p className="text-sm font-medium text-gray-500 mb-1">{title}</p>
        <h3 className={`text-2xl font-bold ${color}`}>{formatMoney(value || 0)}</h3>
      </div>
      <div className={`p-3 rounded-full ${color.replace('text-', 'bg-').replace('700', '100').replace('600', '100')} ${color}`}>
        <Icon size={24} />
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Gestão Financeira</h1>
          <p className="text-gray-500">Fluxo de caixa, despesas e resultados.</p>
        </div>
        
        <div className="flex gap-2">
           <input 
             type="month" 
             className="bg-white border rounded-lg px-3 py-2 text-sm font-medium"
             value={filterMonth}
             onChange={e => setFilterMonth(e.target.value)}
           />
           <button onClick={() => handleOpenNew('expense')} className="bg-red-600 text-white px-4 py-2 rounded-lg font-bold flex items-center gap-2 hover:bg-red-700">
             <TrendingDown size={18} /> Despesa
           </button>
           <button onClick={() => handleOpenNew('income')} className="bg-green-600 text-white px-4 py-2 rounded-lg font-bold flex items-center gap-2 hover:bg-green-700">
             <TrendingUp size={18} /> Receita
           </button>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <StatCard title="Receitas (Entradas)" value={stats.totalIncome} color="text-green-600" icon={TrendingUp} />
          <StatCard title="Despesas (Saídas)" value={stats.totalExpense} color="text-red-600" icon={TrendingDown} />
          <StatCard title="Saldo do Mês" value={stats.balance} color={stats.balance >= 0 ? "text-blue-600" : "text-red-600"} icon={DollarSign} />
          <StatCard title="A Pagar (Pendente)" value={stats.pendingExpense} color="text-orange-600" icon={AlertCircle} />
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="flex border-b">
           <button 
             onClick={() => setActiveTab('dashboard')}
             className={`px-6 py-4 font-bold text-sm border-b-2 transition-colors ${activeTab === 'dashboard' ? 'border-[#FF6B35] text-[#FF6B35]' : 'border-transparent text-gray-500'}`}
           >
             Visão Geral
           </button>
           <button 
             onClick={() => setActiveTab('records')}
             className={`px-6 py-4 font-bold text-sm border-b-2 transition-colors ${activeTab === 'records' ? 'border-[#FF6B35] text-[#FF6B35]' : 'border-transparent text-gray-500'}`}
           >
             Lançamentos Detalhados
           </button>
        </div>

        <div className="p-6">
           {activeTab === 'dashboard' ? (
             <div className="h-80 w-full">
               <h3 className="font-bold text-gray-700 mb-4">Fluxo Diário</h3>
               <ResponsiveContainer width="100%" height="100%">
                 <BarChart data={stats?.dailyFlow || []}>
                   <XAxis dataKey="day" />
                   <YAxis />
                   <Tooltip formatter={(value: number) => formatMoney(value)} />
                   <Bar dataKey="income" fill="#10B981" name="Entradas" radius={[4, 4, 0, 0]} />
                   <Bar dataKey="expense" fill="#EF4444" name="Saídas" radius={[4, 4, 0, 0]} />
                 </BarChart>
               </ResponsiveContainer>
             </div>
           ) : (
             <div className="space-y-4">
                <div className="flex gap-2 mb-4">
                   <button onClick={() => setFilterType('all')} className={`px-3 py-1 rounded text-sm ${filterType === 'all' ? 'bg-gray-800 text-white' : 'bg-gray-100'}`}>Todos</button>
                   <button onClick={() => setFilterType('income')} className={`px-3 py-1 rounded text-sm ${filterType === 'income' ? 'bg-green-600 text-white' : 'bg-gray-100'}`}>Entradas</button>
                   <button onClick={() => setFilterType('expense')} className={`px-3 py-1 rounded text-sm ${filterType === 'expense' ? 'bg-red-600 text-white' : 'bg-gray-100'}`}>Saídas</button>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm min-w-[600px]">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="p-3">Data</th>
                        <th className="p-3">Descrição</th>
                        <th className="p-3">Categoria</th>
                        <th className="p-3 text-right">Valor</th>
                        <th className="p-3 text-center">Status</th>
                        <th className="p-3 w-10"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {records
                        .filter(r => filterType === 'all' || r.type === filterType)
                        .map(rec => (
                        <tr key={rec.id} className="hover:bg-gray-50">
                          <td className="p-3 font-mono text-gray-600">{new Date(rec.dueDate).toLocaleDateString()}</td>
                          <td className="p-3">
                             <div className="font-medium text-gray-800">{rec.description}</div>
                             {rec.linkedEntityId && <span className="text-[10px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded">Automático</span>}
                          </td>
                          <td className="p-3 text-gray-500">{// @ts-ignore 
                          FINANCIAL_CATEGORIES[rec.category]?.label || rec.category}</td>
                          <td className={`p-3 text-right font-bold ${rec.type === 'income' ? 'text-green-600' : 'text-red-600'}`}>
                             {rec.type === 'income' ? '+' : '-'} {formatMoney(rec.amount)}
                          </td>
                          <td className="p-3 text-center">
                             {rec.status === 'paid' ? (
                               <span className="inline-flex items-center gap-1 text-green-600 bg-green-50 px-2 py-1 rounded text-xs font-bold">
                                 <CheckCircle size={12} /> PAGO
                               </span>
                             ) : (
                               <span className="inline-flex items-center gap-1 text-orange-600 bg-orange-50 px-2 py-1 rounded text-xs font-bold">
                                 PENDENTE
                               </span>
                             )}
                          </td>
                          <td className="p-3 flex gap-2 justify-end">
                             {rec.status === 'pending' && !rec.linkedEntityId && (
                               <button onClick={() => handleMarkPaid(rec)} className="text-green-500 hover:text-green-700" title="Marcar como Pago"><CheckCircle size={16}/></button>
                             )}
                             {!rec.linkedEntityId && (
                               <button onClick={() => handleDelete(rec.id)} className="text-gray-400 hover:text-red-600" title="Excluir"><Trash2 size={16}/></button>
                             )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
             </div>
           )}
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
           <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
              <h3 className="font-bold text-lg mb-4 text-gray-800">
                {formData.type === 'income' ? 'Nova Receita' : 'Nova Despesa'}
              </h3>
              
              <form onSubmit={handleSave} className="space-y-4">
                 <div>
                    <label className="block text-sm font-medium mb-1">Descrição</label>
                    <input required className="w-full border p-2 rounded" value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} />
                 </div>
                 
                 <div className="grid grid-cols-2 gap-4">
                    <div>
                       <label className="block text-sm font-medium mb-1">Valor (R$)</label>
                       <input 
                         required 
                         type="number" 
                         step="0.01" 
                         className="w-full border p-2 rounded" 
                         value={formData.amount} 
                         onChange={e => setFormData({...formData, amount: parseFloat(e.target.value)})} 
                       />
                    </div>
                    <div>
                       <label className="block text-sm font-medium mb-1">Vencimento</label>
                       <input required type="date" className="w-full border p-2 rounded" value={typeof formData.dueDate === 'string' ? formData.dueDate : (formData.dueDate instanceof Date ? formData.dueDate.toISOString().split('T')[0] : '')} onChange={e => setFormData({...formData, dueDate: e.target.value})} />
                    </div>
                 </div>

                 <div>
                    <label className="block text-sm font-medium mb-1">Categoria</label>
                    <select required className="w-full border p-2 rounded bg-white" value={formData.category} onChange={e => setFormData({...formData, category: e.target.value})}>
                       <option value="">Selecione...</option>
                       {Object.entries(FINANCIAL_CATEGORIES).map(([key, val]: any) => (
                          <option key={key} value={key}>{val.label}</option>
                       ))}
                    </select>
                 </div>

                 {formData.category === 'salario' && (
                    <div className="bg-gray-50 p-3 rounded border">
                       <label className="block text-xs font-bold text-gray-500 mb-1">Funcionário</label>
                       <input className="w-full border p-2 rounded text-sm" placeholder="Nome do Colaborador" 
                         value={formData.metadata?.salaryEmployeeName || ''}
                         onChange={e => setFormData({ ...formData, metadata: { ...formData.metadata, salaryEmployeeName: e.target.value } })}
                       />
                    </div>
                 )}

                 {formData.category === 'financiamento' && (
                    <div className="bg-gray-50 p-3 rounded border grid grid-cols-2 gap-2">
                       <div>
                         <label className="block text-xs font-bold text-gray-500 mb-1">Parcela Atual</label>
                         <input type="number" className="w-full border p-2 rounded text-sm" 
                           value={formData.metadata?.loanCurrentInstallment || ''}
                           onChange={e => setFormData({ ...formData, metadata: { ...formData.metadata, loanCurrentInstallment: Number(e.target.value) } })}
                         />
                       </div>
                       <div>
                         <label className="block text-xs font-bold text-gray-500 mb-1">Total Parcelas</label>
                         <input type="number" className="w-full border p-2 rounded text-sm" 
                           value={formData.metadata?.loanTotalInstallments || ''}
                           onChange={e => setFormData({ ...formData, metadata: { ...formData.metadata, loanTotalInstallments: Number(e.target.value) } })}
                         />
                       </div>
                    </div>
                 )}

                 <div className="flex gap-3 pt-4">
                    <button type="button" onClick={() => setShowModal(false)} className="flex-1 py-2 border rounded">Cancelar</button>
                    <button type="submit" className={`flex-1 py-2 text-white rounded font-bold ${formData.type === 'income' ? 'bg-green-600' : 'bg-red-600'}`}>
                       Salvar
                    </button>
                 </div>
              </form>
           </div>
        </div>
      )}
    </div>
  );
};

export default Financials;
